import React, { ChangeEvent, FunctionComponent } from "react";

import { Caption, CheckBoxComponentValue, ColSpan, GridColumnCount, GridGapSize, GridLayout, GridLayoutItem } from "@multiweb/reactComponents";
import { bemClassNames } from "@multiweb/reactUtils";
import { IAttrValue } from "@multiweb/types/contracts";
import { LOC_BASIC_CONDITION, LOC_CALLED_FUNCTION, LOC_COMMENT, LOC_STATIC, LOC_TEXT_PARAMETER, LOC_USE_CURRENT_OBJECT, LOC_USE_PARAMETRIC_FORM_OBJECT } from "../../../../../../locKeys";
import { parseStringToBoolean } from "../../../../../../utils";
import { CONST_ACTION_COMMENT_AT, CONST_ACTION_CONDITION_AT, CONST_CALL_METHOD_DEFAULT_PARAM_VALUE_AT, CONST_CALL_METHOD_INSERT_ACTUAL_OBJECT_AT, CONST_CALL_METHOD_INSERT_PARAM_FORM_OBJECT_AT, CONST_CALL_METHOD_METHOD_NAME_AT, CONST_CALL_METHOD_STATIC_AT } from "../../../../../../workflowParser/constants";
import { Memo, TextLOVEdit, WfCheckBox } from "../../../../components";
import { MAX_INPUT_LENGTH_L } from "../constants";
import { ActionPageProps } from "../types";

const getMethodNameAttrs = ["Patri_ke_tride.Nazev_tridy", "Fyzicky_nazev", "Static"];

const classNames = bemClassNames("call-method-page");
const checkboxesContainer = classNames("checkboxes-container");

export const CallMethodPage: FunctionComponent<ActionPageProps> = ({ hostKey, properties, updateAction }) => {
    const onMethodNameChange = (e: ChangeEvent<HTMLInputElement>) => {
        updateAction([{ key: CONST_CALL_METHOD_METHOD_NAME_AT, value: e.target.value }]);
    };

    const getMethodName = (results?: readonly IAttrValue[]) => {
        if (results) {
            const isStatic = parseStringToBoolean(results[2].toString(), false);
            const methodName = isStatic ? `${results[0]}.${results[1]}` : results[1].toString();

            updateAction([
                { key: CONST_CALL_METHOD_METHOD_NAME_AT, value: methodName },
                { key: CONST_CALL_METHOD_STATIC_AT, value: results[2].toString() },
            ]);
        }
    };

    const onParamValueChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        updateAction([{ key: CONST_CALL_METHOD_DEFAULT_PARAM_VALUE_AT, value: e.target.value }]);
    };

    const onInsertParamFormObject = (value: CheckBoxComponentValue) => {
        updateAction([{ key: CONST_CALL_METHOD_INSERT_PARAM_FORM_OBJECT_AT, value: value ? "true" : "false" }]);
    };

    const onInsertActualObjectChange = (value: CheckBoxComponentValue) => {
        updateAction([{ key: CONST_CALL_METHOD_INSERT_ACTUAL_OBJECT_AT, value: value ? "true" : "false" }]);
    };

    const onConditionChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        updateAction([{ key: CONST_ACTION_CONDITION_AT, value: e.target.value }]);
    };

    const onCommentChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        updateAction([{ key: CONST_ACTION_COMMENT_AT, value: e.target.value }]);
    };

    return (
        <GridLayout columns={GridColumnCount.two} gap={GridGapSize.two}>
            <GridLayoutItem colSpan={ColSpan.two}>
                <Caption required locKey={LOC_CALLED_FUNCTION}/>
                <TextLOVEdit
                    qa={LOC_CALLED_FUNCTION}
                    required
                    formName={"FRM.CFunkce.CFunkce"}
                    attributeNames={getMethodNameAttrs}
                    value={properties[CONST_CALL_METHOD_METHOD_NAME_AT]}
                    maxLength={MAX_INPUT_LENGTH_L}
                    onChange={onMethodNameChange}
                    onFormClose={getMethodName}
                    {...{ hostKey }} />
            </GridLayoutItem>
            <GridLayoutItem colSpan={ColSpan.two}>
                <WfCheckBox
                    qa={LOC_STATIC}
                    disabled
                    showFalseAsCrossed
                    textLocKey={LOC_STATIC}
                    value={parseStringToBoolean(properties[CONST_CALL_METHOD_STATIC_AT], false)} {...{ hostKey }} />
            </GridLayoutItem>
            <GridLayoutItem colSpan={ColSpan.two}>
                <Caption locKey={LOC_TEXT_PARAMETER}/>
                <Memo qa={LOC_TEXT_PARAMETER} value={properties[CONST_CALL_METHOD_DEFAULT_PARAM_VALUE_AT]} onChange={onParamValueChange} {...{ hostKey }} />
            </GridLayoutItem>
            <GridLayoutItem colSpan={ColSpan.two}>
                <div className={checkboxesContainer}>
                    <WfCheckBox
                        qa={LOC_USE_PARAMETRIC_FORM_OBJECT}
                        showFalseAsCrossed
                        value={parseStringToBoolean(properties[CONST_CALL_METHOD_INSERT_PARAM_FORM_OBJECT_AT], false)}
                        textLocKey={LOC_USE_PARAMETRIC_FORM_OBJECT}
                        onChange={onInsertParamFormObject} {...{ hostKey }} />
                    <WfCheckBox
                        qa={LOC_USE_CURRENT_OBJECT}
                        showFalseAsCrossed
                        value={parseStringToBoolean(properties[CONST_CALL_METHOD_INSERT_ACTUAL_OBJECT_AT], true)}
                        textLocKey={LOC_USE_CURRENT_OBJECT}
                        onChange={onInsertActualObjectChange} {...{ hostKey }} />
                </div>
            </GridLayoutItem>
            <GridLayoutItem colSpan={ColSpan.two}>
                <Caption locKey={LOC_BASIC_CONDITION} />
                <Memo qa={LOC_BASIC_CONDITION} value={properties[CONST_ACTION_CONDITION_AT]} onChange={onConditionChange} {...{ hostKey }} />
            </GridLayoutItem>
            <GridLayoutItem colSpan={ColSpan.two}>
                <Caption locKey={LOC_COMMENT} />
                <Memo qa={LOC_COMMENT} value={properties[CONST_ACTION_COMMENT_AT]} onChange={onCommentChange} {...{ hostKey }} />
            </GridLayoutItem>
        </GridLayout>
    );
};
